import { watch } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import type { RouteLocationNormalized } from "vue-router";
import i18n from "@/i18n";
import type { RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth.store";
import { useSeoProjectsStore } from "@/stores/seoProjects.store";
import { ensureSharedSession } from "@/composables/useSharedAuth";
import {
  anchorPlanProjectWorkspaceBeforeEnter,
  anchorPlanSettingsBeforeEnter,
  sanitizeAnchorPlanProjectQuery,
} from "@/router/anchorPlanGuards.ts";

// Импорт страниц
const LandingPage = () => import("@/views/LandingPage.vue");
const LoginPage = () => import("@/views/auth/LoginPage.vue");
const RegisterPage = () => import("@/views/auth/RegisterPage.vue");
const AuthCallback = () => import("@/views/auth/AuthCallback.vue");
const DashboardPage = () => import("@/views/DashboardPage.vue");
const ProjectsPage = () => import("@/views/projects/ProjectsPage.vue");
const ProjectDetailsPage = () =>
  import("@/views/projects/ProjectDetailsPage.vue");
const ProjectWorkspace = () => import("@/views/projects/ProjectWorkspace.vue");
const KeywordsPage = () => import("@/views/keywords/KeywordsPage.vue");
const CleaningPage = () => import("@/views/cleaning/CleaningPage.vue");
// ClustersPage removed - using ProjectWorkspace with ClustersTools instead
const ExportsPage = () => import("@/views/exports/ExportsPage.vue");
const SettingsPage = () => import("@/views/settings/SettingsPage.vue");
const ProfilePage = () => import("@/views/ProfilePage.vue");
const NotFoundPage = () => import("@/views/NotFoundPage.vue");
const StatisticsPage = () => import("@/pages/StatisticsPage.vue");
const HubDashboardPage = () => import("@/pages/HubDashboardPage.vue");
const MaintenancePage = () => import("@/pages/MaintenancePage.vue");
const RoleManagement = () => import("@/views/admin/RoleManagement.vue");
const VersionHistoryPage = () => import("@/pages/VersionHistoryPage.vue");
const SeoToolsCatalog = () => import("@/views/SeoToolsCatalog.vue");
const TrafficForecastPage = () => import("@/views/TrafficForecastPage.vue");
const CwvProjectsPage = () => import("@/views/cwv/CwvProjectsPage.vue");
const CwvProjectPage = () => import("@/views/cwv/CwvProjectPage.vue");
const CpMobilePage = () => import("@/views/CpMobilePage.vue");
const SeoAhrefsPage = () => import("@/views/SeoAhrefsPage.vue");
const MaudauValidatorPage = () => import("@/views/MaudauValidatorPage.vue");
const ViyarTranslatorPage = () => import("@/views/ViyarTranslatorPage.vue");
const KnowledgeBasePage = () => import("@/views/KnowledgeBasePage.vue");
const WebarchiveProPage = () =>
  import("@/views/webarchive/WebarchiveProPage.vue");
const AnchorPlanLayout = () => import("@/layouts/AnchorPlanLayout.vue");
const AnchorPlanProjectsPage = () =>
  import("@/views/anchor-plan/AnchorPlanProjectsPage.vue");
const AnchorPlanCreatePage = () =>
  import("@/views/anchor-plan/AnchorPlanCreatePage.vue");
const AnchorPlanProjectWorkspace = () =>
  import("@/views/anchor-plan/AnchorPlanProjectWorkspace.vue");
const AnchorPlanDetailPage = () =>
  import("@/views/anchor-plan/AnchorPlanDetailPage.vue");
const AnchorPlanCollaboratorCsvPage = () =>
  import("@/views/anchor-plan/AnchorPlanCollaboratorCsvPage.vue");
const AnchorPlanAdminLayout = () =>
  import("@/layouts/AnchorPlanAdminLayout.vue");
const AnchorPlanStatisticsLayout = () =>
  import("@/views/anchor-plan/AnchorPlanStatisticsLayout.vue");
const AdminStatsOverviewPage = () =>
  import("@/views/anchor-plan/admin-statistics/AdminStatsOverviewPage.vue");
const AdminStatsTrendsPage = () =>
  import("@/views/anchor-plan/admin-statistics/AdminStatsTrendsPage.vue");
const AdminStatsTeamsPage = () =>
  import("@/views/anchor-plan/admin-statistics/AdminStatsTeamsPage.vue");
const AdminStatsEconomyPage = () =>
  import("@/views/anchor-plan/admin-statistics/AdminStatsEconomyPage.vue");
const AdminStatsFunnelPage = () =>
  import("@/views/anchor-plan/admin-statistics/AdminStatsFunnelPage.vue");
const AdminStatsComparePage = () =>
  import("@/views/anchor-plan/admin-statistics/AdminStatsComparePage.vue");
const AdminStatsExternalPage = () =>
  import("@/views/anchor-plan/admin-statistics/AdminStatsExternalPage.vue");
const AdminStatsUsersPage = () =>
  import("@/views/anchor-plan/admin-statistics/AdminStatsUsersPage.vue");
const AdminStatsProjectsPage = () =>
  import("@/views/anchor-plan/admin-statistics/AdminStatsProjectsPage.vue");
const AdminStatsExportPage = () =>
  import("@/views/anchor-plan/admin-statistics/AdminStatsExportPage.vue");
const AnchorPlanRolesPage = () =>
  import("@/views/anchor-plan/AnchorPlanRolesPage.vue");
const AnchorPlanMaintenancePage = () =>
  import("@/views/anchor-plan/AnchorPlanMaintenancePage.vue");
const AnchorPlanLoadPage = () =>
  import("@/views/anchor-plan/AnchorPlanLoadPage.vue");
const AnchorPlanLogsPage = () =>
  import("@/views/anchor-plan/AnchorPlanLogsPage.vue");
const AnchorPlanAdminProjectPage = () =>
  import("@/views/anchor-plan/AnchorPlanAdminProjectPage.vue");
const AnchorPlanAdminUserPage = () =>
  import("@/views/anchor-plan/AnchorPlanAdminUserPage.vue");

// Макеты
const AuthLayout = () => import("@/layouts/AuthLayout.vue");
const AppLayout = () => import("@/layouts/AppLayout.vue");

const routes: Array<RouteRecordRaw> = [
  // Лендинг страница
  {
    path: "/",
    name: "landing",
    component: LandingPage,
    meta: {
      title: "router.titles.landing",
      public: true,
    },
  },

  // Аутентификация
  {
    path: "/auth",
    component: AuthLayout,
    meta: { public: true },
    children: [
      {
        path: "login",
        name: "login",
        component: LoginPage,
        meta: {
          title: "router.titles.login",
        },
      },
      {
        path: "register",
        name: "register",
        component: RegisterPage,
        meta: {
          title: "router.titles.register",
        },
      },
      {
        path: "callback",
        name: "auth-callback",
        component: AuthCallback,
        meta: {
          title: "router.titles.authCallback",
        },
      },
    ],
  },

  // Anchor Plan (fullscreen, без AppLayout; лише після входу)
  {
    path: "/app/anchor-plan",
    component: AnchorPlanLayout,
    meta: { requiresAuth: true, seoProject: "anchor-plan" },
    children: [
      {
        path: "",
        name: "anchor-plan-home",
        component: AnchorPlanProjectsPage,
        meta: {
          public: true,
          title: "router.titles.anchorPlanProjects",
        },
      },
      {
        path: "create-project",
        name: "anchor-plan-create",
        component: AnchorPlanCreatePage,
        meta: {
          public: true,
          title: "router.titles.anchorPlanCreate",
        },
      },
      {
        path: "collaborator-csv",
        name: "anchor-plan-collaborator-csv",
        component: AnchorPlanCollaboratorCsvPage,
        meta: {
          public: true,
          title: "router.titles.anchorPlanCollaboratorCsv",
        },
      },
      {
        path: "project/:projectId/settings",
        redirect: (to) => ({
          name: "anchor-plan-project-settings",
          params: {
            projectId: String(to.params.projectId),
            settingsTab: "budget",
          },
          query: sanitizeAnchorPlanProjectQuery("settings", to.query),
          hash: to.hash,
        }),
        meta: {
          public: true,
        },
      },
      {
        path: "project/:projectId/settings/:settingsTab",
        name: "anchor-plan-project-settings",
        component: AnchorPlanProjectWorkspace,
        beforeEnter: anchorPlanSettingsBeforeEnter,
        meta: {
          public: true,
          title: "router.titles.anchorPlan",
        },
      },
      {
        path: "project/:projectId/url-detail",
        name: "anchor-plan-project-url-detail",
        component: AnchorPlanDetailPage,
        meta: {
          public: true,
          title: "router.titles.anchorPlanUrlDetail",
        },
      },
      {
        path: "project/:projectId/:section?",
        name: "anchor-plan-project",
        component: AnchorPlanProjectWorkspace,
        beforeEnter: anchorPlanProjectWorkspaceBeforeEnter,
        meta: {
          public: true,
          title: "router.titles.anchorPlan",
        },
      },
      {
        path: "detail",
        redirect: (to) => {
          const q = to.query || {};
          const pid = String(q.project ?? q.p ?? q.id ?? "");
          if (!pid) return { name: "anchor-plan-home" };
          const query: Record<string, string | string[]> = {};
          const url = q.url;
          const plan = q.plan;
          if (typeof url === "string") query.url = url;
          if (Array.isArray(url) && url[0]) query.url = url[0];
          if (typeof plan === "string") query.plan = plan;
          if (Array.isArray(plan) && plan[0]) query.plan = plan[0];
          return {
            name: "anchor-plan-project-url-detail",
            params: { projectId: pid },
            query,
          };
        },
        meta: {
          public: true,
        },
      },
      {
        path: "admin",
        component: AnchorPlanAdminLayout,
        meta: { requiresAuth: true },
        redirect: { name: "anchor-plan-admin-statistics-overview" },
        children: [
          {
            path: "statistics",
            component: AnchorPlanStatisticsLayout,
            meta: {
              public: true,
              title: "router.titles.anchorPlanStats",
            },
            redirect: { name: "anchor-plan-admin-statistics-overview" },
            children: [
              {
                path: "overview",
                name: "anchor-plan-admin-statistics-overview",
                component: AdminStatsOverviewPage,
                meta: { title: "router.titles.anchorPlanStatsOverview" },
              },
              {
                path: "trends",
                name: "anchor-plan-admin-statistics-trends",
                component: AdminStatsTrendsPage,
                meta: { title: "router.titles.anchorPlanStatsTrends" },
              },
              {
                path: "teams",
                name: "anchor-plan-admin-statistics-teams",
                component: AdminStatsTeamsPage,
                meta: { title: "router.titles.anchorPlanStatsTeams" },
              },
              {
                path: "economy",
                name: "anchor-plan-admin-statistics-economy",
                component: AdminStatsEconomyPage,
                meta: { title: "router.titles.anchorPlanStatsEconomy" },
              },
              {
                path: "funnel",
                name: "anchor-plan-admin-statistics-funnel",
                component: AdminStatsFunnelPage,
                meta: { title: "router.titles.anchorPlanStatsFunnel" },
              },
              {
                path: "compare",
                name: "anchor-plan-admin-statistics-compare",
                component: AdminStatsComparePage,
                meta: { title: "router.titles.anchorPlanStatsCompare" },
              },
              {
                path: "external",
                name: "anchor-plan-admin-statistics-external",
                component: AdminStatsExternalPage,
                meta: { title: "router.titles.anchorPlanStatsExternal" },
              },
              {
                path: "users",
                name: "anchor-plan-admin-statistics-users",
                component: AdminStatsUsersPage,
                meta: { title: "router.titles.anchorPlanStatsUsers" },
              },
              {
                path: "projects",
                name: "anchor-plan-admin-statistics-projects",
                component: AdminStatsProjectsPage,
                meta: { title: "router.titles.anchorPlanStatsProjects" },
              },
              {
                path: "export",
                name: "anchor-plan-admin-statistics-export",
                component: AdminStatsExportPage,
                meta: { title: "router.titles.anchorPlanStatsExport" },
              },
            ],
          },
          {
            path: "roles",
            name: "anchor-plan-admin-roles",
            component: AnchorPlanRolesPage,
            meta: {
              public: true,
              title: "router.titles.anchorPlanRoles",
            },
          },
          {
            path: "maintenance",
            name: "anchor-plan-admin-maintenance",
            component: AnchorPlanMaintenancePage,
            meta: {
              public: true,
              title: "router.titles.anchorPlanMaintenance",
            },
          },
          {
            path: "load",
            name: "anchor-plan-admin-load",
            component: AnchorPlanLoadPage,
            meta: {
              public: true,
              title: "router.titles.anchorPlanLoad",
            },
          },
          {
            path: "logs",
            name: "anchor-plan-admin-logs",
            component: AnchorPlanLogsPage,
            meta: {
              public: true,
              title: "router.titles.anchorPlanLogs",
            },
          },
          {
            path: "project/:projectId",
            name: "anchor-plan-admin-project",
            component: AnchorPlanAdminProjectPage,
            meta: {
              public: true,
              title: "router.titles.anchorPlanAdminProject",
            },
          },
          {
            path: "user/:userId",
            name: "anchor-plan-admin-user",
            component: AnchorPlanAdminUserPage,
            meta: {
              public: true,
              title: "router.titles.anchorPlanAdminUser",
            },
          },
        ],
      },
    ],
  },

  // Главное приложение
  {
    path: "/app",
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      // Главная панель
      {
        path: "",
        name: "dashboard",
        component: DashboardPage,
        meta: {
          title: "router.titles.dashboard",
        },
      },

      // Проекты
      {
        path: "projects",
        name: "projects",
        component: ProjectsPage,
        meta: {
          title: "router.titles.projects",
        },
      },

      // Project workspace with tabs
      {
        path: "projects/:projectId",
        redirect: (to) => `/app/projects/${to.params.projectId}/keywords`,
      },
      {
        path: "projects/:projectId/keywords",
        name: "keywords",
        component: ProjectWorkspace,
        meta: {
          title: "router.titles.keywords",
        },
      },
      {
        path: "projects/:projectId/cleaning",
        name: "cleaning",
        component: ProjectWorkspace,
        meta: {
          title: "router.titles.cleaning",
        },
      },
      {
        path: "projects/:projectId/clusters",
        name: "clusters",
        component: ProjectWorkspace,
        meta: {
          title: "router.titles.clusters",
        },
      },
      {
        path: "projects/:projectId/tdh",
        name: "tdh",
        component: ProjectWorkspace,
        meta: {
          title: "router.titles.tdh",
        },
      },
      {
        path: "projects/:projectId/tz-maker",
        name: "tz-maker",
        component: ProjectWorkspace,
        meta: {
          title: "router.titles.tzMaker",
        },
      },

      // Экспорт
      {
        path: "projects/:projectId/exports",
        name: "exports",
        component: ExportsPage,
        meta: {
          title: "router.titles.exports",
        },
      },

      // Настройки
      {
        path: "settings",
        name: "settings",
        component: SettingsPage,
        meta: {
          title: "router.titles.settings",
        },
      },

      // Профиль
      {
        path: "profile",
        name: "profile",
        component: ProfilePage,
        meta: {
          title: "router.titles.profile",
        },
      },

      // SEO Tools Catalog (available to all users)
      {
        path: "seo-tools",
        name: "seo-tools",
        component: SeoToolsCatalog,
        meta: {
          title: "router.titles.seoTools",
          seoProject: "seo-tools",
        },
      },

      // Traffic Forecast
      {
        path: "traffic-forecast",
        name: "traffic-forecast",
        component: TrafficForecastPage,
        meta: {
          title: "router.titles.trafficForecast",
          seoProject: "traffic-forecast",
        },
      },

      {
        path: "cwv",
        name: "cwv",
        component: CwvProjectsPage,
        meta: {
          title: "Core Web Vitals - Semantica Promodo",
          seoProject: "cwv",
          wide: true,
        },
      },
      {
        path: "cwv/:projectId",
        name: "cwv-project",
        component: CwvProjectPage,
        meta: {
          title: "Core Web Vitals - Semantica Promodo",
          seoProject: "cwv",
          wide: true,
        },
      },

      // Mobile commercial proposal automation
      {
        path: "cp-mobile",
        name: "cp-mobile",
        component: CpMobilePage,
        meta: {
          title: "router.titles.cpMobile",
          seoProject: "cp-mobile",
        },
      },

      // Ahrefs commercial proposal automation
      {
        path: "seo-ahrefs",
        name: "seo-ahrefs",
        component: SeoAhrefsPage,
        meta: {
          title: "router.titles.seoAhrefs",
          seoProject: "seo-ahrefs",
        },
      },

      // Knowledge Base
      {
        path: "knowledge-base",
        name: "knowledge-base",
        component: KnowledgeBasePage,
        meta: {
          title: "router.titles.knowledgeBase",
          seoProject: "knowledge-base",
        },
      },

      // История версий
      {
        path: "version-history",
        name: "version-history",
        component: VersionHistoryPage,
        meta: {
          title: "router.titles.versionHistory",
        },
      },

      // Admin Role Management (admins only)
      {
        path: "admin/role-management",
        name: "role-management",
        component: RoleManagement,
        meta: {
          requiresAdmin: true,
          title: "router.titles.roleManagement",
        },
      },

      {
        path: "hub-dashboard",
        name: "hub-dashboard",
        component: HubDashboardPage,
        meta: {
          requiresAdmin: true,
          title: "router.titles.hubDashboard",
        },
      },

      // Statistics (admins only)
      {
        path: "statistics",
        name: "statistics",
        component: StatisticsPage,
        meta: {
          requiresAdmin: true,
          title: "router.titles.statistics",
        },
      },

      // Maintenance (admins only)
      {
        path: "maintenance",
        name: "maintenance",
        component: MaintenancePage,
        meta: {
          requiresAdmin: true,
          title: "router.titles.maintenance",
          wide: true,
        },
      },
    ],
  },

  // Maudau TDH Validator - отдельный роут без /app но с layout
  {
    path: "/maudau",
    component: AppLayout,
    meta: { requiresAuth: true, seoProject: "maudau" },
    children: [
      {
        path: "",
        name: "maudau",
        component: MaudauValidatorPage,
        meta: {
          title: "router.titles.maudau",
        },
      },
    ],
  },

  // Viyar Translator - отдельный роут без /app но с layout
  {
    path: "/viyar-translator",
    component: AppLayout,
    meta: { requiresAuth: true, seoProject: "viyar-translator" },
    children: [
      {
        path: "",
        name: "viyar-translator",
        component: ViyarTranslatorPage,
        meta: {
          title: "router.titles.viyarTranslator",
        },
      },
    ],
  },

  // Webarchive Pro - мониторинг изменений на страницах
  {
    path: "/webarchive-pro",
    component: AppLayout,
    meta: { requiresAuth: true, seoProject: "webarchive-pro" },
    children: [
      {
        path: "",
        name: "webarchive-pro",
        component: WebarchiveProPage,
        meta: { title: "router.titles.webarchive", webarchiveBranding: true },
      },
      {
        path: "project-:projectId",
        name: "webarchive-project",
        component: WebarchiveProPage,
        meta: { title: "router.titles.webarchive", webarchiveBranding: true },
      },
      {
        path: "project-:projectId/site-:siteId",
        name: "webarchive-site",
        component: WebarchiveProPage,
        meta: { title: "router.titles.webarchive", webarchiveBranding: true },
      },
      {
        path: "search",
        name: "webarchive-global-search",
        component: () => import("@/views/webarchive/GlobalArchiveSearch.vue"),
        meta: { title: "router.titles.webarchiveSearch", webarchiveBranding: true },
      },
      {
        path: "archive/:canonicalId",
        name: "webarchive-canonical-timeline",
        component: () =>
          import("@/views/webarchive/ArchiveTimelineReadonly.vue"),
        meta: { title: "router.titles.webarchiveArchive", webarchiveBranding: true },
      },
      {
        path: "snapshot/:snapshotId",
        name: "webarchive-snapshot-view",
        component: () => import("@/views/webarchive/SnapshotViewPage.vue"),
        meta: { title: "router.titles.webarchiveSnapshot", webarchiveBranding: true },
      },
    ],
  },

  // Position Checker — регулярна перевірка позицій через DataForSEO
  {
    path: "/position-checker",
    component: AppLayout,
    meta: { requiresAuth: true, seoProject: "position-checker", wide: true },
    children: [
      {
        path: "",
        name: "position-checker",
        component: () =>
          import("@/views/positionChecker/PositionCheckerPage.vue"),
        meta: { title: "router.titles.positionChecker" },
      },
      {
        path: "project-:projectId/:section?",
        name: "position-checker-project",
        component: () =>
          import("@/views/positionChecker/PositionCheckerPage.vue"),
        meta: { title: "router.titles.positionChecker" },
      },
    ],
  },

  // Перенаправления
  {
    path: "/dashboard",
    redirect: "/app",
  },
  {
    path: "/projects",
    redirect: "/app/projects",
  },

  // 404
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: NotFoundPage,
    meta: {
      title: "router.titles.notFound",
      public: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    // Query-only change on the same path (e.g. deep-link cleanup, filters): keep the
    // current scroll position instead of jumping to the top.
    if (to.path === from.path) {
      return false;
    }
    return { top: 0 };
  },
});

// Останній заголовок, який поставив роутер. Сторінки можуть ставити свій
// (назва проєкту в Anchor Plan) — тоді при зміні мови його не перезаписуємо.
let lastRouteTitle: string | null = null;

function routeTitle(route: RouteLocationNormalized): string | null {
  const key = route.meta.title;
  return typeof key === "string" && key ? i18n.global.t(key) : null;
}

function applyRouteTitle(route: RouteLocationNormalized): void {
  const title = routeTitle(route);
  if (title) {
    document.title = title;
    lastRouteTitle = title;
  }
}

watch(i18n.global.locale, () => {
  const appTitles = (["ua", "en"] as const).map((locale) =>
    i18n.global.t("app.title", {}, { locale }),
  );
  const ownTitle =
    lastRouteTitle === null ||
    document.title === lastRouteTitle ||
    appTitles.includes(document.title);
  if (ownTitle) applyRouteTitle(router.currentRoute.value);
});

// Глобальные navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);

  // Захищені маршрути — чекаємо сесію до рендеру. Anchor Plan (у т.ч. url-detail у новій вкладці)
  // не блокуємо: layout і сторінка з’являються одразу, /api/user догружається у фоні.
  if (requiresAuth) {
    await ensureSharedSession();
  } else if (to.path.startsWith("/app/anchor-plan")) {
    void ensureSharedSession();
  }

  // Проверка аутентификации
  if (requiresAuth && !authStore.isAuthenticated) {
    // Сохраняем путь, куда пользователь пытался попасть
    next({
      name: "landing",
      query: { redirect: to.fullPath },
    });
    return;
  }

  // Проверка прав администратора
  if (requiresAdmin && authStore.user?.role !== "admin") {
    // Если требуются права админа, но пользователь не админ
    next({ name: "dashboard" });
    return;
  }

  const seoProject = to.matched
    .map((record) => record.meta.seoProject)
    .find((slug): slug is string => typeof slug === "string");

  if (seoProject) {
    const seoProjects = useSeoProjectsStore();
    seoProjects.noteRole(authStore.user?.role);
    if (!requiresAuth && !authStore.isAuthenticated) {
      await ensureSharedSession();
      seoProjects.noteRole(authStore.user?.role);
    }
    await seoProjects.ensureLoaded(authStore.isAuthenticated);
    if (!seoProjects.canAccess(seoProject)) {
      next(
        authStore.isAuthenticated
          ? { name: "dashboard" }
          : { name: "landing" },
      );
      return;
    }
  }

  if (to.name === "login" && authStore.isAuthenticated) {
    // Проверяем, есть ли сохраненный путь для редиректа
    const redirect = to.query.redirect as string;
    if (redirect) {
      next(redirect);
    } else {
      next({ name: "dashboard" });
    }
    return;
  }

  // Установка заголовка страницы (meta.title — ключ перекладу)
  applyRouteTitle(to);

  next();
});

// Обработка ошибок навигации
router.onError((error) => {
  console.error("Router error:", error);
});

export default router;
